import React, { useEffect, useState } from "react";
import {
  Wallet, AlertTriangle, Clock, BadgeCheck, Users, School, TrendingUp, ArrowUpRight,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { formatFCFA } from "../lib/format";
import { PageHeader, Spinner } from "../components/common";
import { Card } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";

const KPI = ({ icon: Icon, label, value, tone, testid, onClick, sub }) => (
  <Card
    data-testid={testid}
    onClick={onClick}
    className={`p-6 border-slate-200/80 ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
  >
    <div className="flex items-start justify-between">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
      {onClick && <ArrowUpRight className="h-4 w-4 text-slate-300" />}
    </div>
    <p className="mt-4 text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
    <p className="mt-1 font-mono text-2xl font-bold tracking-tight text-slate-900">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <Spinner />;

  const barColors = ["#0d9488", "#14b8a6", "#06b6d4", "#0891b2"];

  return (
    <div>
      <PageHeader
        title={`Bonjour, ${user?.name?.split(" ")[0]}`}
        subtitle="Vue d'ensemble de l'activite commerciale de Scolaris Central."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPI testid="kpi-revenue" icon={Wallet} tone="bg-teal-50 text-teal-600" label="Recettes encaissees" value={formatFCFA(stats.total_revenue)} sub={`Facture: ${formatFCFA(stats.total_billed)}`} />
        <KPI testid="kpi-outstanding" icon={AlertTriangle} tone="bg-amber-50 text-amber-600" label="Soldes impayes" value={formatFCFA(stats.outstanding)} sub={`Dont retard: ${formatFCFA(stats.overdue_amount)}`} onClick={() => navigate("/factures")} />
        <KPI testid="kpi-pending" icon={Clock} tone="bg-rose-50 text-rose-600" label="Validations en attente" value={stats.pending_approvals} sub="A traiter par l'Admin Principal" onClick={() => navigate("/paiements")} />
        <KPI testid="kpi-subscriptions" icon={BadgeCheck} tone="bg-sky-50 text-sky-600" label="Abonnements actifs" value={stats.active_subscriptions} sub={`${stats.establishments} etablissements`} onClick={() => navigate("/abonnements")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Encaissements mensuels</h3>
              <p className="text-sm text-slate-500">Paiements valides par mois</p>
            </div>
            <TrendingUp className="h-5 w-5 text-teal-500" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.monthly_collection}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`} />
              <Tooltip formatter={(v) => formatFCFA(v)} cursor={{ fill: "#f8fafc" }} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Bar dataKey="amount" radius={[6, 6, 0, 0]} fill="#0d9488" barSize={44} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">Recettes par formule</h3>
          <p className="text-sm text-slate-500 mb-5">Repartition encaissee</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.revenue_by_plan} layout="vertical">
              <XAxis type="number" hide tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`} />
              <YAxis type="category" dataKey="plan" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} width={70} />
              <Tooltip formatter={(v) => formatFCFA(v)} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Bar dataKey="amount" radius={[0, 6, 6, 0]} barSize={28}>
                {stats.revenue_by_plan.map((_, i) => <Cell key={i} fill={barColors[i % barColors.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
        <Card className="p-6 flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100"><Users className="h-6 w-6 text-slate-700" /></div>
          <div><p className="font-mono text-2xl font-bold text-slate-900">{stats.customers}</p><p className="text-sm text-slate-500">Clients / groupes scolaires</p></div>
        </Card>
        <Card className="p-6 flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100"><School className="h-6 w-6 text-slate-700" /></div>
          <div><p className="font-mono text-2xl font-bold text-slate-900">{stats.establishments}</p><p className="text-sm text-slate-500">Etablissements rattaches</p></div>
        </Card>
      </div>
    </div>
  );
}
